import React from 'react';
import PropTypes from 'prop-types';

import Button from '@decisiv/ui-components/lib/components/Button';

import useAccountAccess from '../../hooks/useAccountAccess';
import useAddToAccount from '../../hooks/useAddToAccount';
import { CancelLink, FormAlert } from '../styles';
import AccountAccessTable from './AccountAccessTable';
import AddToAccountModal from './AddToAccountModal';
import PermissionsSection from './PermissionsSection';
import RemoveRestoreModal from './RemoveRestoreModal';
import { PERMISSIONS_SECTION } from './copy';
import { Actions } from './styles';

/**
 * The Access & Permissions tab: Accounts (PD-4069) stacked above Permissions &
 * Discoverability (PD-4070), one tab built from two tickets.
 *
 * The Permissions & Discoverability half used to be the tab's sole content,
 * with a read-only Accounts placeholder above it that was removed rather than
 * shipped inert — this is PD-4069 filling that placeholder in for real, so the
 * two now live in one file/folder instead of two that happened to render into
 * the same KeyDS Tab.
 *
 * The two halves keep their own state: this calls useAccountAccess itself for
 * the Accounts section, while `access` (the whole return of
 * useAccessPermissionsForm) arrives as a prop, since EditUserContent also
 * needs it for the header's live role badges.
 */
function AccessPermissionsTab({
  access,
  accountAccess,
  canEdit,
  email,
  fullName,
  userId,
  username,
  usersPath,
}) {
  const {
    canActOn,
    closeModal,
    confirm,
    isSubmitting,
    isTerminal,
    modalError,
    openRemoveModal,
    openRestoreModal,
    pendingAction,
    pendingAccount,
    accounts,
    setAccounts,
  } = useAccountAccess(userId, accountAccess);

  const {
    accountOptions,
    accountsTotal,
    close: closeAddToAccountModal,
    hasMoreAccounts,
    isLoadingAccounts,
    isOpen: isAddToAccountModalOpen,
    isSubmitting: isAddingToAccount,
    loadMoreAccounts,
    modalError: addToAccountError,
    open: openAddToAccountModal,
    searchAccounts,
    selectedAccountIds,
    setSelectedAccountIds,
    submit: submitAddToAccount,
  } = useAddToAccount(userId, { onAdded: setAccounts });

  const {
    canSave,
    columns,
    errors,
    groups,
    isAvailable,
    isSaving,
    isValid,
    mode,
    notice,
    save,
    setCell,
    setMode,
    showScopeChoice,
  } = access;

  return (
    <>
      <AccountAccessTable
        canActOn={canActOn}
        canEdit={canEdit}
        onAdd={openAddToAccountModal}
        onRemove={openRemoveModal}
        onRestore={openRestoreModal}
        accounts={accounts}
      />

      <RemoveRestoreModal
        action={pendingAction}
        email={email}
        error={modalError}
        isSubmitting={isSubmitting}
        isTerminal={isTerminal}
        onClose={closeModal}
        onConfirm={confirm}
        account={pendingAccount}
        username={username}
        visible={Boolean(pendingAccount)}
      />

      <AddToAccountModal
        accountOptions={accountOptions}
        accountsTotal={accountsTotal}
        email={email}
        error={addToAccountError}
        fullName={fullName}
        hasMoreAccounts={hasMoreAccounts}
        isLoadingAccounts={isLoadingAccounts}
        isSubmitting={isAddingToAccount}
        selectedAccountIds={selectedAccountIds}
        visible={isAddToAccountModalOpen}
        onChangeSelectedAccounts={setSelectedAccountIds}
        onClose={closeAddToAccountModal}
        onLoadMoreAccounts={loadMoreAccounts}
        onSearchAccounts={searchAccounts}
        onSubmit={submitAddToAccount}
      />

      {/* Inert when the server sent no access payload — an environment where
          the user holds no role in any segment account has nothing to draw.
          The Accounts section above still renders regardless: an empty
          account list is already a valid, handled state for it. */}
      {isAvailable && (
        <>
          <PermissionsSection
            columns={columns}
            disabled={!canEdit}
            errors={errors.byAccount}
            groups={groups}
            isValid={isValid}
            mode={mode}
            notice={notice}
            onCellChange={setCell}
            onModeChange={setMode}
            showScopeChoice={showScopeChoice}
          />

          {/* Persistent rather than only in the save toast, which auto-dismisses
              after eight seconds: these are the messages saying why the tab
              refuses to save at all. Same reasoning as UserDetailsForm's
              FormAlert. */}
          {errors.base.length > 0 && (
            <FormAlert role="alert">
              {errors.base.map((message) => (
                <span key={message}>{message}</span>
              ))}
            </FormAlert>
          )}

          {canEdit && (
            <Actions>
              <Button
                disabled={!canSave}
                loading={isSaving}
                loadingText={PERMISSIONS_SECTION.saving}
                onClick={save}
                text={PERMISSIONS_SECTION.save}
              />
              <CancelLink href={usersPath}>{PERMISSIONS_SECTION.cancel}</CancelLink>
            </Actions>
          )}
        </>
      )}
    </>
  );
}

AccessPermissionsTab.propTypes = {
  /**
   * The whole return of useAccessPermissionsForm. Only the keys this tab
   * reads are declared; the hook also returns isSelf, lockFor, rolesFor and
   * teams, which nothing here consumes.
   */
  access: PropTypes.shape({
    canSave: PropTypes.bool,
    columns: PropTypes.array,
    errors: PropTypes.object,
    groups: PropTypes.array,
    isAvailable: PropTypes.bool,
    isSaving: PropTypes.bool,
    isValid: PropTypes.bool,
    mode: PropTypes.string,
    notice: PropTypes.string,
    save: PropTypes.func,
    setCell: PropTypes.func,
    setMode: PropTypes.func,
    showScopeChoice: PropTypes.bool,
  }).isRequired,
  accountAccess: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      decisivId: PropTypes.string,
      name: PropTypes.string,
      dealerType: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.string),
      removedAt: PropTypes.string,
    }),
  ),
  canEdit: PropTypes.bool,
  email: PropTypes.string.isRequired,
  fullName: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  username: PropTypes.string.isRequired,
  usersPath: PropTypes.string.isRequired,
};

AccessPermissionsTab.defaultProps = {
  accountAccess: [],
  canEdit: false,
};

export default AccessPermissionsTab;
